import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import AlbumArt from './AlbumArt';
import ScrollingText from './ScrollingText';
import TipAlert from './TipAlert';

// /overlay: a see-through page for OBS (Browser source), not linked from the site. Only ever shows what the site already
// shows everyone: the logo, my avatar, and what I'm listening to (myActivity, kept live by the activity cog).
//   /overlay                        everything together
//   /overlay?show=logo              only some of it, to place each part yourself as its own source (logo, avatar, music)
//   /overlay?demo=1                 a sample track, for lining things up when nothing's playing
//   /overlay?show=tip               the tip animation, on its own (not part of "everything"): see TipAlert.jsx

const STAGE_WIDTH = 900;  // drawn at this size, then scaled to fit the source, so it's sharp at any resolution
const TRACK_GAP_MS = 5000; // Discord can drop the activity between tracks: don't hide the card for that
const AVATAR_OVERLAP = 70; // how far the avatar sits over the card's right edge
const DEMO = { type: 'Listening to', name: 'YouTube Music', detail: 'A sample song with quite a long title to show it scrolling', state: 'Sample Artist', image: '/pfp.jpg', startedAt: Date.now() - 83000 };
// The letters in cheeks_logo.svg aren't in reading order: where C, H, E, E, K and S are in its group of letters
const LETTER_ORDER = [1, 3, 5, 4, 2, 0];

const CSS = `
  html, body, #root { background: transparent !important; overflow: hidden; }
  @keyframes overlayLetter {
    0%, 42%, 100% { transform: translateY(0) scale(1, 1); }
    8%  { transform: translateY(1px) scale(1.06, 0.92); }
    18% { transform: translateY(-9px) scale(0.97, 1.05); }
    28% { transform: translateY(0) scale(1.04, 0.95); }
    35% { transform: translateY(-2.5px) scale(1, 1); }
  }
  .overlay-letter { transform-box: fill-box; transform-origin: 50% 100%; animation: overlayLetter 2.6s ease-in-out infinite; }
  @keyframes overlayCheek {
    0%, 58%, 100% { transform: scale(1, 1); }
    6%  { transform: scale(1.12, 1); }
    13% { transform: scale(1, 1.12); }
    20% { transform: scale(1.09, 1); }
    27% { transform: scale(1, 1.07); }
    34% { transform: scale(1.04, 1); }
    41% { transform: scale(1, 1.024); }
    48% { transform: scale(1.01, 1); }
  }
  .overlay-cheek { position: absolute; inset: 0; animation: overlayCheek 2.8s ease-in-out infinite; }
  .overlay-cheek img, .overlay-layer { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
  .overlay-cheek.right { clip-path: circle(15% at 81.2% 62.7%); transform-origin: 81.2% 77.7%; animation-delay: 0.1s; }
  .overlay-cheek.left { clip-path: circle(16.2% at 56.3% 63.6%); transform-origin: 56.3% 79.8%; }
  /* Togepi dancing while music plays, about 120 beats a minute (Discord doesn't send the song's real beat). His feet stay
     planted and he sways and bends from them, dipping on every beat. The ring and the lettering stay still */
  @keyframes overlayGroove {
    0%, 100% { transform: skewX(0deg) scale(1.03, 0.95); }
    12.5% { transform: skewX(-5deg) scale(0.99, 1.02); }
    25%   { transform: skewX(-9deg) translateX(-5px) scale(1.03, 0.95); }
    37.5% { transform: skewX(-4deg) scale(0.99, 1.03); }
    50%   { transform: skewX(0deg) scale(1.03, 0.95); }
    62.5% { transform: skewX(5deg) scale(0.99, 1.02); }
    75%   { transform: skewX(9deg) translateX(5px) scale(1.03, 0.95); }
    87.5% { transform: skewX(4deg) scale(0.99, 1.03); }
  }
  .overlay-toge { position: absolute; inset: 0; transform-origin: 48% 88%; }
  .overlay-toge.dancing { animation: overlayGroove 2s ease-in-out infinite; }
  /* His cheeks shake on the beat while he dances */
  @keyframes overlayCheekDance {
    0%, 100% { transform: scale(1.1, 0.98) rotate(-2deg); }
    25% { transform: scale(0.99, 1.1) rotate(1deg); }
    50% { transform: scale(1.08, 0.99) rotate(2deg); }
    75% { transform: scale(1, 1.08) rotate(-1deg); }
  }
  .overlay-toge.dancing .overlay-cheek { animation: overlayCheekDance 0.5s ease-in-out infinite; }
  @keyframes overlayGlint {
    0%, 62% { transform: translateX(-190px); opacity: 0.32; }
    86%, 100% { transform: translateX(300px); opacity: 0.32; }
  }
  .overlay-glint { animation: overlayGlint 4.5s ease-in-out infinite; }
  @keyframes overlayBar { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
`;

// cheeks_logo.svg, drawn inline so each letter can move: the same file, so a new logo just needs the file replacing
function BouncingLogo() {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let stopped = false;
    fetch('/cheeks_logo.svg').then(r => r.text()).then((text) => {
      if (stopped) return;
      const svg = new DOMParser().parseFromString(text, 'image/svg+xml');
      const back = svg.querySelector('svg > path');
      const letters = [...svg.querySelectorAll('g > path')];
      const stops = [...svg.querySelectorAll('linearGradient stop')].map(s => s.getAttribute('stop-color'));
      if (back && letters.length === LETTER_ORDER.length) {
        setLogo({ back: back.getAttribute('d'), letters: LETTER_ORDER.map(i => letters[i].getAttribute('d')), stops });
      }
    }).catch(() => {});
    return () => { stopped = true; };
  }, []);

  if (!logo) return <div style={{ width: 620, aspectRatio: '223.64 / 93.54' }} />;
  return (
    // Extra room around the viewBox, so the letters aren't cut off at the top of their bounce
    <svg viewBox="-8 -14 239.64 115.54" width={660} style={{ display: 'block', overflow: 'visible', filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.55))' }} role="img" aria-label="Cheeks">
      <defs>
        <linearGradient id="overlay-logo" x1="43.45" y1="-21.55" x2="175.3" y2="110.31" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={logo.stops[0]} />
          <stop offset="1" stopColor={logo.stops[1]} />
        </linearGradient>
      </defs>
      <path d={logo.back} fill="url(#overlay-logo)" />
      {logo.letters.map((d, i) => (
        <path key={i} d={d} fill="#fff" className="overlay-letter" style={{ animationDelay: `${i * 0.11}s` }} />
      ))}
    </svg>
  );
}

// Togepi's sunglasses: drawn in the cut-out's own 1200 x 1200 space, over his eyes and at the tilt of his head
const LENS = 'M-152,-50 L-24,-50 Q-13,-50 -14,-39 L-19,4 Q-25,50 -72,50 L-104,50 Q-150,50 -156,4 L-162,-38 Q-163,-50 -152,-50 Z';
function Sunglasses() {
  return (
    <svg viewBox="0 0 1200 1200" className="overlay-layer" aria-hidden="true">
      <defs>
        <clipPath id="overlay-lenses">
          <path d={LENS} />
          <path d={LENS} transform="scale(-1 1)" />
        </clipPath>
      </defs>
      <g transform="translate(392 400) rotate(-21)">
        {/* The arms, then the bridge, then the lenses */}
        <path d="M-160,-34 L-212,-22 M160,-34 L232,-22" stroke="#050507" strokeWidth="20" strokeLinecap="round" fill="none" />
        <rect x="-22" y="-44" width="44" height="18" fill="#050507" />
        <path d={LENS} fill="#0b0b10" stroke="#050507" strokeWidth="14" strokeLinejoin="round" />
        <path d={LENS} transform="scale(-1 1)" fill="#0b0b10" stroke="#050507" strokeWidth="14" strokeLinejoin="round" />
        {/* The glint, sliding across every few seconds */}
        <g clipPath="url(#overlay-lenses)">
          <path className="overlay-glint" d="M-60,-70 L-20,-70 L-90,70 L-130,70 Z" fill="#fff" />
          <path className="overlay-glint" d="M-5,-70 L10,-70 L-60,70 L-75,70 Z" fill="#fff" />
        </g>
      </g>
    </svg>
  );
}

// My avatar in layers, made from pfp.jpg and toge-cheeks.png: the ring behind, Togepi, and the lettering in front. The
// circle stays still and only Togepi moves: his cheeks bounce, and he dances while there's music
function Avatar({ dancing }) {
  return (
    <div style={{ position: 'relative', zIndex: 1, flexShrink: 0, width: 210, height: 210, borderRadius: '50%', overflow: 'hidden', background: '#000', boxShadow: '0 8px 24px rgba(0,0,0,0.55), 0 0 0 4px rgba(255,255,255,0.14)' }}>
      <img src="/avatar-ring.webp" alt="" className="overlay-layer" />
      <div className={`overlay-toge${dancing ? ' dancing' : ''}`}>
        <img src="/avatar-toge.webp" alt="" className="overlay-layer" />
        <div className="overlay-cheek right" aria-hidden="true"><img src="/avatar-toge.webp" alt="" /></div>
        <div className="overlay-cheek left" aria-hidden="true"><img src="/avatar-toge.webp" alt="" /></div>
        <Sunglasses />
      </div>
      <img src="/avatar-text.webp" alt="" className="overlay-layer" />
    </div>
  );
}

const Bars = () => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 3, height: 16 }} aria-hidden="true">
    {[0, 0.25, 0.1, 0.35].map((delay, i) => (
      <span key={i} style={{ width: 4, height: '100%', borderRadius: 2, background: '#23a55a', transformOrigin: 'bottom', animation: `overlayBar 0.9s ease-in-out ${delay}s infinite` }} />
    ))}
  </span>
);

const clock = (ms) => {
  const secs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

// What I'm listening to, live: only music, so games and the like stay off the stream. `playing` goes false when it stops
function useTrack(database, demo) {
  const [track, setTrack] = useState(demo ? DEMO : null);
  const [playing, setPlaying] = useState(demo);
  const gap = useRef(null);

  useEffect(() => {
    if (demo) return undefined;
    const off = onValue(ref(database, 'myActivity'), (snapshot) => {
      const activity = snapshot.val();
      clearTimeout(gap.current);
      if (activity && (activity.type || '').toLowerCase().includes('listen')) {
        setTrack(activity);
        setPlaying(true);
      } else {
        gap.current = setTimeout(() => setPlaying(false), TRACK_GAP_MS);
      }
    }, () => {});
    return () => { off(); clearTimeout(gap.current); };
  }, [database, demo]);

  return { track, playing };
}

// The music card: slides away when nothing's playing
function NowPlaying({ track, visible, besideAvatar }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const title = track?.detail || track?.name || '';
  const source = track?.detail ? track?.name : null;
  // With the avatar over its right edge, the card is wider by what's covered, so the text keeps its room
  const covered = besideAvatar ? AVATAR_OVERLAP : 0;
  const width = 590 + covered;
  return (
    <div style={{ width: visible ? width : 0, marginRight: visible ? -covered : 0, opacity: visible ? 1 : 0, overflow: 'hidden', flexShrink: 0, transition: 'width 0.6s ease, margin-right 0.6s ease, opacity 0.5s ease', padding: '14px 0' }}>
      <div style={{ width, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 18, padding: `16px ${16 + covered}px 16px 16px`, borderRadius: 20, background: 'rgba(12,12,15,0.86)', border: '1px solid rgba(255,255,255,0.14)' }}>
        <AlbumArt src={track?.image} size={118} style={{ borderRadius: 12 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
            <Bars />
            <span style={{ color: '#23a55a', fontWeight: 700, fontSize: 17, letterSpacing: '0.09em' }}>NOW PLAYING</span>
          </div>
          <ScrollingText text={title} maxWidth="420px" style={{ color: '#fff', fontWeight: 700, fontSize: 31, lineHeight: 1.2 }} />
          {track?.state && <ScrollingText text={track.state} maxWidth="420px" style={{ color: '#d4d4d8', fontWeight: 500, fontSize: 23, lineHeight: 1.25 }} />}
          {(source || track?.startedAt) && (
            <div style={{ color: '#a1a1aa', fontSize: 17, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
              {[source, track?.startedAt ? clock(Date.now() - track.startedAt) : null].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Overlay({ database }) {
  const params = new URLSearchParams(window.location.search);
  const parts = (params.get('show') || 'logo,avatar,music').toLowerCase().split(',');
  const demo = params.has('demo');
  const fit = () => Math.min(window.innerWidth, window.innerHeight) / STAGE_WIDTH;
  const [scale, setScale] = useState(fit);
  const { track, playing } = useTrack(database, demo);

  useEffect(() => {
    const resize = () => setScale(fit());
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      {parts.includes('tip') && <TipAlert loop={demo} at={params.has('at') ? Number(params.get('at')) || 0 : null} />}
      <div style={{ position: 'fixed', top: 0, left: '50%', width: STAGE_WIDTH, marginLeft: -STAGE_WIDTH / 2, transform: `scale(${scale})`, transformOrigin: 'top center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, paddingTop: 24 }}>
        {parts.includes('logo') && <BouncingLogo />}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {parts.includes('music') && <NowPlaying track={track} visible={playing} besideAvatar={parts.includes('avatar')} />}
          {parts.includes('avatar') && <Avatar dancing={playing} />}
        </div>
      </div>
    </>
  );
}
